﻿import { Intent } from '@blueprintjs/core';
import { ButtonSave, Button } from 'components/buttons';
import { FormDateInput, FormLocationSelector, FormHelperText } from 'components/form-fields';
import { AlertUnsavedData, Modal, NotificationInline, NotificationToaster } from 'components/notifications';
import { useDirtyData } from 'hooks/useDirtyData';
import React, { useEffect, useState, Fragment } from 'react';
import { JobService } from 'services';
import { UserAccess } from 'components/user-access/UserAccess';
import { PageRestricted } from 'pages/errors/page-restricted';
import PropTypes from 'prop-types';
import moment from "moment";

import './BulkEdit.css';
export function BulkEditJob(
    {
        loading = false,
        closeBulkEditModal = null,
        showBulkEditModal = false,
        jobs,
        locations
    }
) {
    const defaultBulkEdit = {
        dueDate: null,
        locationId: null,
        changeDueDate: false,
        changeLocation: false,
        jobIds: jobs.map(job => parseInt(job))
    };

    const [bulkEdit, setBulkEdit] = useState(defaultBulkEdit);
    const [originalBulkEdit, setOriginalBulkEdit] = useState(defaultBulkEdit);
    const [loadingValue, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activating, setActivating] = useState(false);
    const isDataDirty = useDirtyData(originalBulkEdit, bulkEdit);
    const requiredAction = ["Internal"];
    const minDate = moment().toDate();

    const [locationList, setLocationList] = useState({ location: [], hierarchy: [] });
    const [selectedBELocations, setSelectedBELocations] = useState([]);
    const [showLocationsAsHierarachy, setShowLocationsAsHierarachy] = useState(true);

    const [canActivateWarning, setCanActivateWarning] = useState ([]);
    const [response, setResponse] = useState(getResponse( false, false));
    useEffect(initialLoad, [locations]);
    useEffect(checkCanActivate, [jobs, showBulkEditModal]);

    useEffect(onShowBulkEditModal, [showBulkEditModal]);

    function onShowBulkEditModal() {
        if (showBulkEditModal) {

            //clear any previous results
            setResponse(getResponse(false, false));
        }
    }

    function initialLoad() {
        setLocationList(locations);
        setLoading(false);
    }

    function checkCanActivate() {
        setLoading(true);
        setCanActivateWarning([]);

        if (!showBulkEditModal || jobs.length === 0) {
            setLoading(false);
            return;
        }

        JobService.canActivate(jobs.map(job => parseInt(job))).then(r => {
            let data = [];

            if (r != null) {
                data = r.filter(function (obj) {
                    return !obj.canActivate;
                }).map(function (obj) { return { jobId: obj.jobId, reason: obj.cannotActivateReason } });

                if (data.length > 0) {
                    let message = [{ message: "The jobs cannot be activated because: " }];
                    data.forEach(function (obj) {
                        message.push({ message: `Job ${obj.jobId} - ${obj.reason}` });

                    });
                    setCanActivateWarning(message);
                }
                setLoading(false);
            }

        }, function () {
            NotificationToaster.show(Intent.DANGER, "Bulk edit could not be loaded. Please try loading the page again.");
            setLoading(false);
        });

    }
    function onSave() {
        setSaving(true);
        var bulkEditDetails = {
            jobIds: jobs.map(job => parseInt(job)),
            dueDate: bulkEdit.dueDate,
            locationId: bulkEdit.locationId
        };

        JobService.saveBulkEdit(bulkEditDetails)
            .then(
                () => {
                    NotificationToaster.show(Intent.SUCCESS, "Jobs updated successfully.");
                    resetForm();
                    setSaving(false);
                    setResponse(getResponse(true, response.isActivated));
                    checkCanActivate();
                },
                () => {
                    NotificationToaster.show(Intent.DANGER, "Could not update jobs. Please try again.");
                    setSaving(false);
                },
        );
    }

    function onActivate() {
        setActivating(true);
        JobService.activateJobs(jobs)
            .then(
                () => {
                    NotificationToaster.show(Intent.SUCCESS, "Jobs activated successfully.");
                    resetForm();
                    setActivating(false);
                    setResponse(getResponse(response.isUpdated, true));
                },
                () => {
                    NotificationToaster.show(Intent.DANGER, "Could not activate jobs. Please try again.");
                    setActivating(false);
                },
            );
    }

    function onDueDateChange(item) {
        setBulkEdit((previousValue) => {
            return {
                ...previousValue,
                dueDate: item ? moment(item).format("YYYY-MM-DD") + ' 00:00:00' : null
            }
        });
    }

    function onLocationSelect(locationsValue) {

        var lastLocation = locationsValue.length > 0 ? locationsValue.pop() : null;

        setBulkEdit((previousValue) => {
            return {
                ...previousValue,
                locationId: lastLocation
            }
        });

        setSelectedBELocations(lastLocation == null ? [] : [lastLocation]);
    }

    function getResponse(isUpdated, isActivated) {
        return {
            isUpdated: isUpdated,
            isActivated: isActivated
        }
    }

    function setUseHierarchy() {
        setShowLocationsAsHierarachy(previous => !previous);
    }

    function closeBulkEditModalValue() {
        resetForm();
        closeBulkEditModal(response);
    }

    function resetForm() {
        setBulkEdit(defaultBulkEdit);
        setOriginalBulkEdit(defaultBulkEdit);
        setSelectedBELocations([]);
    }
    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <Fragment>
                    <Modal
                        isOpen={showBulkEditModal}
                        title="Bulk Edit"
                        showCloseButton={false}
                        clickOutsideClose={false}
                        wide={true}
                    >
                        <Fragment>
                            <div className="scrollable-500y">
                                <NotificationInline
                                    allowClose={false}
                                    show
                                    text={"Please note, this will update " + jobs.length + " job" + (jobs.length === 1 ? "" : "s" + ".")}
                                    intent={Intent.WARNING}
                                />
                                <NotificationInline
                                    allowClose={false}
                                    show={canActivateWarning.length !== 0}
                                    messages={canActivateWarning}
                                    intent={Intent.WARNING}
                                />
                                <FormHelperText loading={loadingValue}>{`If a due date is set then all the jobs will have the due date changed to this due date when saved.`}</FormHelperText>
                                <FormDateInput
                                    id="field-job-due-date"
                                    disabled={saving || response.isActivated}
                                    headingText="Due date:"
                                    onChange={onDueDateChange}
                                    value={bulkEdit.dueDate}
                                    loading={loadingValue}
                                    minDate={minDate}
                                ></FormDateInput>
                                <FormHelperText loading={loadingValue}>{`If a location is set then all the jobs will have the location changed to this location when saved.`}</FormHelperText>
                                <FormLocationSelector
                                    headingText={`Visit Location:`}
                                    businessArea={"Tacho"}
                                    loading={loadingValue}
                                    selectedLocations={selectedBELocations}
                                    setSelectedLocations={onLocationSelect}
                                    useHierarchy={showLocationsAsHierarachy}
                                    setUseHierarchy={setUseHierarchy}
                                    useLocationDefaults={false}
                                    useCustomLocations
                                    customLocations={locationList}
                                    locationDefaultsToTrue
                                    singleSelection
                                    disabled={saving || response.isActivated}
                                />
                                <AlertUnsavedData
                                    isDirty={isDataDirty}>
                                </AlertUnsavedData>
                            </div>
                        </Fragment>

                        <div className="bp6-dialog-footer">
                            <div className="bp6-dialog-footer-actions">
                                <ButtonSave text={"Save"} icon={null} intent={Intent.DANGER} onClick={onSave} simpleDisabled={!isDataDirty || activating || loadingValue || response.isActivated } disabled={saving}></ButtonSave>
                                <ButtonSave text={"Activate"} icon={null} intent={Intent.DANGER} onClick={onActivate} simpleDisabled={canActivateWarning.length !== 0 || isDataDirty || saving || loadingValue || response.isActivated } disabled={activating}></ButtonSave>
                                <Button text="Close" onClick={() => { closeBulkEditModalValue(); }} disabled={saving}></Button>
                            </div>
                        </div>
                    </Modal>
                </Fragment>

            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

BulkEditJob.propTypes = {
    jobs: PropTypes.array,
    locations: PropTypes.object,
    loading: PropTypes.bool.isRequired,
    closeBulkEditModal: PropTypes.func,
    showBulkEditModal: PropTypes.bool.isRequired
};